/**
 * LE DOUTE, CÔTÉ CONTENU — ce qui fait remonter un mail malgré l'autonomie (PLAN-LEARNING-LOOPS § 7.2).
 *
 * Le doute a deux moitiés, chacune à un seul endroit :
 *   - le CONTEXTE (premier échange, destinataire ajouté, un humain intervenu récemment, version
 *     rejetée, compte exclu, Otto qui a lui-même posé une question, plafond du jour) est lu en SQL
 *     (`autonomy_mail_doubts`), où vivent les données ;
 *   - le CONTENU (ce que le mail engage, l'humeur du client, une pièce jointe) est lu ici, au
 *     bout du canal, sur le texte exact qui partirait.
 *
 * Vocabulaire FERMÉ, comme les prémisses de mission : une raison est un code, jamais une phrase
 * libre, pour qu'elle se compte, se filtre et s'affiche pareil partout. Le doute ne rejette rien :
 * le brouillon reste en validation, avec sa raison, sous les yeux de Gilles.
 *
 * Pourquoi des expressions et pas un modèle : « gates en code, pas en prompt » (CLAUDE.md). Un
 * faux positif coûte un clic à Gilles ; un faux négatif enverrait une promesse de prix sans
 * relecture. Les motifs sont donc larges, et l'asymétrie est voulue.
 *
 * Pure, sans IO.
 */
import { missingAnnouncedAttachment } from "./attachmentPromise.ts";
import { mailText, ownText } from "./mailText.ts";

// Re-exported: gmail-send and the tests read them from here.
export { mailText, ownText };

export const CONTENT_DOUBTS = [
  "engagement_prix",
  "engagement_remise",
  "engagement_date",
  "engagement_contrat",
  "engagement_resiliation",
  "engagement_remboursement",
  "engagement_juridique",
  "client_mecontent",
  "piece_jointe",
  "piece_jointe_annoncee",
  "brouillon_modifie",
] as const;
export type ContentDoubt = (typeof CONTENT_DOUBTS)[number];

const HUMAN_RECENT_LABEL = "tu es intervenu récemment dans le fil";

/** Libellés courts, en français, tels que Travail les affiche (SQL et contenu). */
export const DOUBT_LABELS: Readonly<Record<string, string>> = {
  aucune_procedure: "aucune procédure pour ce travail",
  compte_exclu: "compte exclu de l'autonomie",
  premier_echange: "premier échange avec ce destinataire",
  nouveau_fil: "nouveau fil",
  destinataire_ajoute: "destinataire absent du fil",
  // Written under a first code named after the owner until 08/10/2026 (PLAN-GENERICITE § 4 quinquies,
  // step 5, phase B); that alias is gone since no stored send, draft or event carries it (checked 08/10).
  human_recent: HUMAN_RECENT_LABEL,
  version_rejetee: "une version précédente a été rejetée",
  otto_demande: "Otto t'a posé une question sur cette mission",
  plafond_atteint: "plafond d'envois autonomes du jour atteint",
  etat_inconnu: "envoi interrompu, à vérifier dans Gmail",
  competence_fermee: "la compétence n'est plus ouverte",
  version_humaine: "tu as corrigé ce texte : c'est à toi de l'envoyer",
  opposition_prospection: "un destinataire s'est opposé à la prospection",
  engagement_prix: "parle de prix",
  engagement_remise: "parle de remise ou de geste commercial",
  engagement_date: "promet une date",
  engagement_contrat: "parle de contrat ou d'engagement",
  engagement_resiliation: "parle de résiliation",
  engagement_remboursement: "parle de remboursement ou d'avoir",
  engagement_juridique: "sujet juridique",
  client_mecontent: "le client semble mécontent",
  piece_jointe: "pièce jointe",
  piece_jointe_annoncee: "annonce une pièce jointe absente",
  brouillon_modifie: "brouillon modifié ou absent dans Gmail",
};

const ENGAGEMENTS: ReadonlyArray<readonly [ContentDoubt, RegExp]> = [
  ["engagement_prix", /(\d[\d\s.,]*\s?(€|eur\b|euros?\b|ht\b|ttc\b))|\b(prix|tarifs?|devis chiffré|coûte|coût|facturer?|montant)\b/iu],
  ["engagement_remise", /\b(remises?|réductions?|rabais|ristournes?|gratuit(e|ement)?|offert(e|s)?|geste commercial|promotion)\b/iu],
  [
    "engagement_date",
    // A promised date, in any form: « d'ici vendredi », « avant le 3 », « sera livré la semaine
    // prochaine », « sous 48 h », « dans deux jours », « lundi prochain » (audit du 25/09 : la
    // livraison promise pour la semaine prochaine passait sans doute).
    /\b(d'ici (lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|demain|la fin|le \d|\d+|quelques|deux|trois|une|un)|avant (le \d|lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|demain|la fin)|au plus tard|je vous garantis|nous garantissons|sans faute|je m'engage|nous nous engageons|(la semaine|le mois|l'année) prochaine?|(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche) prochain|dès demain|demain (matin|midi|soir|dans la journée)|en fin de (semaine|journée|mois)|sous (\d+|quelques|deux|trois|huit|quinze) ?(h\b|heures?|jours?|semaines?)|dans (les )?(\d+|quelques|deux|trois|huit|quinze) (prochaine?s? )?(heures?|jours?|semaines?|mois)|(sera|seront|serez|sera bien) (livrée?s?|prête?s?|disponibles?|en ligne|en place|corrigée?s?|réglée?s?|résolue?s?|installée?s?|activée?s?|opérationnel\w*) (demain|lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche|cette semaine|ce mois|avant|d'ici|sous|dans))\b/iu,
  ],
  ["engagement_contrat", /\b(contrats?|avenants?|conditions générales|cgv|engagement de \d|signature du|bon de commande)\b/iu],
  ["engagement_resiliation", /\b(résili\w*|resili\w*|préavis|fin d'abonnement|désabonn\w*)\b/iu],
  ["engagement_remboursement", /\b(rembours\w*|avoirs?\b|crédit sur votre|restitu\w*)\b/iu],
  ["engagement_juridique", /\b(juridique|avocats?|mise en demeure|tribunal|contentieux|rgpd|litiges?|plainte|huissier)\b/iu],
];

const DISCONTENT =
  /\b(mécontent\w*|inadmissible|inacceptable|scandal\w*|déçu\w*|furieux|furieuse|honteux|lamentable|pas normal|j'exige|je vais résilier|dernière fois|toujours pas|aucune réponse|relance n°|ras le bol)\b/iu;

export interface MailContentFacts {
  /** Le corps exact qui partirait (HTML ou texte). */
  readonly body: string;
  /** Le dernier message reçu du fil, s'il y en a un. */
  readonly lastInbound: string | null;
  /** Nombre de pièces jointes du brouillon Gmail. */
  readonly attachments: number;
  /** Le brouillon Gmail est-il toujours celui qui a été proposé (texte, destinataires, objet) ? */
  readonly draftMatches: boolean;
}

/** Les doutes de contenu, dans l'ordre du vocabulaire. Vide = rien à redire. */
export function detectMailContentDoubts(facts: MailContentFacts): ContentDoubt[] {
  const found = new Set<ContentDoubt>();
  const mine = ownText(facts.body);
  for (const [code, pattern] of ENGAGEMENTS) {
    if (pattern.test(mine)) found.add(code);
  }
  if (facts.lastInbound && DISCONTENT.test(ownText(facts.lastInbound))) found.add("client_mecontent");
  if (facts.attachments > 0) found.add("piece_jointe");
  if (missingAnnouncedAttachment(facts.body, facts.attachments)) found.add("piece_jointe_annoncee");
  if (!facts.draftMatches) found.add("brouillon_modifie");
  return CONTENT_DOUBTS.filter((code) => found.has(code));
}

/** « parle de prix, promet une date » — la raison telle que Travail et le journal la disent. */
export function describeDoubts(codes: readonly string[]): string {
  return codes.map((code) => DOUBT_LABELS[code] ?? code).join(", ");
}
